import { homedir } from "os";
import { join } from "path";
import { getAgentDir } from "../config.js";
import { type ChromeDensity, isChromeDensity } from "./chrome-density.js";
import { DEFAULT_SETTINGS } from "./settings-defaults.js";
import {
	FileSettingsStorage,
	InMemorySettingsStorage,
	type SettingsError,
	type SettingsScope,
	type SettingsStorage,
} from "./settings-storage.js";
import type {
	BranchSummarySettings,
	CompactionSettings,
	PackageSource,
	Settings,
	ThinkingBudgetsSettings,
	TransportSetting,
	WarningSettings,
} from "./settings-types.js";
import { isToolOutputView, LEGACY_TOOL_OUTPUT_VIEWS, type ToolOutputView } from "./tool-output-view.js";
import type { WebtoolsSearchSettings } from "./tools/webtools-shared.js";

/** The `/learn` thresholds, which are flat top-level numeric settings. */
export type LearnSettingKey =
	| "learnMaxSessions"
	| "learnMaxAgeDays"
	| "learnMinRepeats"
	| "learnMinRequestRepeats"
	| "learnMaxProposals";

/** Deep merge settings: project/overrides take precedence, nested objects merge recursively */
function deepMergeSettings(base: Settings, overrides: Settings): Settings {
	const result: Settings = { ...base };

	for (const key of Object.keys(overrides) as (keyof Settings)[]) {
		const overrideValue = overrides[key];
		const baseValue = base[key];

		if (overrideValue === undefined) {
			continue;
		}

		// For nested objects, merge recursively
		if (
			typeof overrideValue === "object" &&
			overrideValue !== null &&
			!Array.isArray(overrideValue) &&
			typeof baseValue === "object" &&
			baseValue !== null &&
			!Array.isArray(baseValue)
		) {
			(result as Record<string, unknown>)[key] = { ...baseValue, ...overrideValue };
		} else {
			// For primitives and arrays, override value wins
			(result as Record<string, unknown>)[key] = overrideValue;
		}
	}

	return result;
}

export type { SettingsError, SettingsScope, SettingsStorage };
export type {
	BranchSummarySettings,
	CompactionSettings,
	ImageSettings,
	PackageSource,
	RetrySettings,
	Settings,
	ThinkingBudgetsSettings,
	TransportSetting,
	WarningSettings,
} from "./settings-types.js";

export class SettingsManager {
	private storage: SettingsStorage;
	private globalSettings: Settings;
	private projectSettings: Settings;
	private settings: Settings;
	private modifiedFields = new Set<keyof Settings>(); // Track global fields modified during session
	private modifiedNestedFields = new Map<keyof Settings, Set<string>>(); // Track global nested field modifications
	private modifiedProjectFields = new Set<keyof Settings>(); // Track project fields modified during session
	private modifiedProjectNestedFields = new Map<keyof Settings, Set<string>>(); // Track project nested field modifications
	private globalSettingsLoadError: Error | null = null; // Track if global settings file had parse errors
	private projectSettingsLoadError: Error | null = null; // Track if project settings file had parse errors
	private writeQueue: Promise<void> = Promise.resolve();
	private errors: SettingsError[];

	private constructor(
		storage: SettingsStorage,
		initialGlobal: Settings,
		initialProject: Settings,
		globalLoadError: Error | null = null,
		projectLoadError: Error | null = null,
		initialErrors: SettingsError[] = [],
	) {
		this.storage = storage;
		this.globalSettings = initialGlobal;
		this.projectSettings = initialProject;
		this.globalSettingsLoadError = globalLoadError;
		this.projectSettingsLoadError = projectLoadError;
		this.errors = [...initialErrors];
		this.settings = deepMergeSettings(this.globalSettings, this.projectSettings);
	}

	/** Create a SettingsManager that loads from files */
	static create(cwd: string, agentDir: string = getAgentDir()): SettingsManager {
		const storage = new FileSettingsStorage(cwd, agentDir);
		return SettingsManager.fromStorage(storage);
	}

	/** Create a SettingsManager from an arbitrary storage backend */
	static fromStorage(storage: SettingsStorage): SettingsManager {
		const globalLoad = SettingsManager.tryLoadFromStorage(storage, "global");
		const projectLoad = SettingsManager.tryLoadFromStorage(storage, "project");
		const initialErrors: SettingsError[] = [];
		if (globalLoad.error) {
			initialErrors.push({ scope: "global", error: globalLoad.error });
		}
		if (projectLoad.error) {
			initialErrors.push({ scope: "project", error: projectLoad.error });
		}

		return new SettingsManager(
			storage,
			globalLoad.settings,
			projectLoad.settings,
			globalLoad.error,
			projectLoad.error,
			initialErrors,
		);
	}

	/** Create an in-memory SettingsManager (no file I/O) */
	static inMemory(settings: Partial<Settings> = {}): SettingsManager {
		const storage = new InMemorySettingsStorage();
		const initialSettings = SettingsManager.migrateSettings(structuredClone(settings) as Record<string, unknown>);
		storage.withLock("global", () => JSON.stringify(initialSettings, null, 2));
		return SettingsManager.fromStorage(storage);
	}

	private static loadFromStorage(storage: SettingsStorage, scope: SettingsScope): Settings {
		let content: string | undefined;
		storage.withLock(scope, (current) => {
			content = current;
			return undefined;
		});

		if (!content) {
			return {};
		}
		const settings = JSON.parse(content);
		return SettingsManager.migrateSettings(settings);
	}

	private static tryLoadFromStorage(
		storage: SettingsStorage,
		scope: SettingsScope,
	): { settings: Settings; error: Error | null } {
		try {
			return { settings: SettingsManager.loadFromStorage(storage, scope), error: null };
		} catch (error) {
			return { settings: {}, error: error as Error };
		}
	}

	/** Migrate old settings format to new format */
	private static migrateSettings(settings: Record<string, unknown>): Settings {
		// Migrate queueMode -> steeringMode
		if ("queueMode" in settings && !("steeringMode" in settings)) {
			settings.steeringMode = settings.queueMode;
			delete settings.queueMode;
		}

		// Migrate enableEmbsearchTools -> enableSemanticIndex. The tool rename to
		// `SearchCodebase` was a clean break, but this key is a user's stored
		// choice, not a tool name: dropping it would silently flip indexing back
		// on for anyone who had deliberately turned it off.
		if ("enableEmbsearchTools" in settings && !("enableSemanticIndex" in settings)) {
			settings.enableSemanticIndex = settings.enableEmbsearchTools;
			delete settings.enableEmbsearchTools;
		}

		// Migrate legacy websockets boolean -> transport enum
		if (!("transport" in settings) && typeof settings.websockets === "boolean") {
			settings.transport = settings.websockets ? "websocket" : "sse";
			delete settings.websockets;
		}

		// Migrate old skills object format to new array format
		if (
			"skills" in settings &&
			typeof settings.skills === "object" &&
			settings.skills !== null &&
			!Array.isArray(settings.skills)
		) {
			const skillsSettings = settings.skills as {
				enableSkillCommands?: boolean;
				customDirectories?: unknown;
			};
			if (skillsSettings.enableSkillCommands !== undefined && settings.enableSkillCommands === undefined) {
				settings.enableSkillCommands = skillsSettings.enableSkillCommands;
			}
			if (Array.isArray(skillsSettings.customDirectories) && skillsSettings.customDirectories.length > 0) {
				settings.skills = skillsSettings.customDirectories;
			} else {
				delete settings.skills;
			}
		}

		// Migrate retry.maxDelayMs -> retry.provider.maxRetryDelayMs
		if (
			"retry" in settings &&
			typeof settings.retry === "object" &&
			settings.retry !== null &&
			!Array.isArray(settings.retry)
		) {
			const retrySettings = settings.retry as Record<string, unknown>;
			const providerSettings =
				typeof retrySettings.provider === "object" && retrySettings.provider !== null
					? (retrySettings.provider as Record<string, unknown>)
					: undefined;
			if (
				typeof retrySettings.maxDelayMs === "number" &&
				(providerSettings?.maxRetryDelayMs === undefined || providerSettings?.maxRetryDelayMs === null)
			) {
				retrySettings.provider = {
					...(providerSettings ?? {}),
					maxRetryDelayMs: retrySettings.maxDelayMs,
				};
			}
			delete retrySettings.maxDelayMs;
		}

		return settings as Settings;
	}

	getGlobalSettings(): Settings {
		return structuredClone(this.globalSettings);
	}

	getProjectSettings(): Settings {
		return structuredClone(this.projectSettings);
	}

	getDefaultSettings(): Settings {
		return structuredClone(DEFAULT_SETTINGS);
	}

	async reload(): Promise<void> {
		await this.writeQueue;
		const globalLoad = SettingsManager.tryLoadFromStorage(this.storage, "global");
		if (!globalLoad.error) {
			this.globalSettings = globalLoad.settings;
			this.globalSettingsLoadError = null;
		} else {
			this.globalSettingsLoadError = globalLoad.error;
			this.recordError("global", globalLoad.error);
		}

		this.modifiedFields.clear();
		this.modifiedNestedFields.clear();
		this.modifiedProjectFields.clear();
		this.modifiedProjectNestedFields.clear();

		const projectLoad = SettingsManager.tryLoadFromStorage(this.storage, "project");
		if (!projectLoad.error) {
			this.projectSettings = projectLoad.settings;
			this.projectSettingsLoadError = null;
		} else {
			this.projectSettingsLoadError = projectLoad.error;
			this.recordError("project", projectLoad.error);
		}

		this.settings = deepMergeSettings(this.globalSettings, this.projectSettings);
	}

	/** Apply additional overrides on top of current settings */
	applyOverrides(overrides: Partial<Settings>): void {
		this.settings = deepMergeSettings(this.settings, overrides);
	}

	/** Mark a global field as modified during this session */
	private markModified(field: keyof Settings, nestedKey?: string): void {
		this.modifiedFields.add(field);
		if (nestedKey) {
			if (!this.modifiedNestedFields.has(field)) {
				this.modifiedNestedFields.set(field, new Set());
			}
			this.modifiedNestedFields.get(field)!.add(nestedKey);
		}
	}

	/** Mark a project field as modified during this session */
	private markProjectModified(field: keyof Settings, nestedKey?: string): void {
		this.modifiedProjectFields.add(field);
		if (nestedKey) {
			if (!this.modifiedProjectNestedFields.has(field)) {
				this.modifiedProjectNestedFields.set(field, new Set());
			}
			this.modifiedProjectNestedFields.get(field)!.add(nestedKey);
		}
	}

	private recordError(scope: SettingsScope, error: unknown): void {
		const normalizedError = error instanceof Error ? error : new Error(String(error));
		this.errors.push({ scope, error: normalizedError });
	}

	private clearModifiedScope(scope: SettingsScope): void {
		if (scope === "global") {
			this.modifiedFields.clear();
			this.modifiedNestedFields.clear();
			return;
		}

		this.modifiedProjectFields.clear();
		this.modifiedProjectNestedFields.clear();
	}

	private enqueueWrite(scope: SettingsScope, task: () => void): void {
		this.writeQueue = this.writeQueue
			.then(() => {
				task();
				this.clearModifiedScope(scope);
			})
			.catch((error) => {
				this.recordError(scope, error);
			});
	}

	private cloneModifiedNestedFields(source: Map<keyof Settings, Set<string>>): Map<keyof Settings, Set<string>> {
		const snapshot = new Map<keyof Settings, Set<string>>();
		for (const [key, value] of source.entries()) {
			snapshot.set(key, new Set(value));
		}
		return snapshot;
	}

	private persistScopedSettings(
		scope: SettingsScope,
		snapshotSettings: Settings,
		modifiedFields: Set<keyof Settings>,
		modifiedNestedFields: Map<keyof Settings, Set<string>>,
	): void {
		this.storage.withLock(scope, (current) => {
			const currentFileSettings = current
				? SettingsManager.migrateSettings(JSON.parse(current) as Record<string, unknown>)
				: {};
			const mergedSettings: Settings = { ...currentFileSettings };
			for (const field of modifiedFields) {
				const value = snapshotSettings[field];
				if (modifiedNestedFields.has(field) && typeof value === "object" && value !== null) {
					const nestedModified = modifiedNestedFields.get(field)!;
					const baseNested = (currentFileSettings[field] as Record<string, unknown>) ?? {};
					const inMemoryNested = value as Record<string, unknown>;
					const mergedNested = { ...baseNested };
					for (const nestedKey of nestedModified) {
						mergedNested[nestedKey] = inMemoryNested[nestedKey];
					}
					(mergedSettings as Record<string, unknown>)[field] = mergedNested;
				} else {
					(mergedSettings as Record<string, unknown>)[field] = value;
				}
			}

			return JSON.stringify(mergedSettings, null, 2);
		});
	}

	private save(): void {
		this.settings = deepMergeSettings(this.globalSettings, this.projectSettings);

		if (this.globalSettingsLoadError) {
			return;
		}

		const snapshotGlobalSettings = structuredClone(this.globalSettings);
		const modifiedFields = new Set(this.modifiedFields);
		const modifiedNestedFields = this.cloneModifiedNestedFields(this.modifiedNestedFields);

		this.enqueueWrite("global", () => {
			this.persistScopedSettings("global", snapshotGlobalSettings, modifiedFields, modifiedNestedFields);
		});
	}

	private saveProjectSettings(settings: Settings): void {
		this.projectSettings = structuredClone(settings);
		this.settings = deepMergeSettings(this.globalSettings, this.projectSettings);

		if (this.projectSettingsLoadError) {
			return;
		}

		const snapshotProjectSettings = structuredClone(this.projectSettings);
		const modifiedFields = new Set(this.modifiedProjectFields);
		const modifiedNestedFields = this.cloneModifiedNestedFields(this.modifiedProjectNestedFields);
		this.enqueueWrite("project", () => {
			this.persistScopedSettings("project", snapshotProjectSettings, modifiedFields, modifiedNestedFields);
		});
	}

	async flush(): Promise<void> {
		await this.writeQueue;
	}

	drainErrors(): SettingsError[] {
		const drained = [...this.errors];
		this.errors = [];
		return drained;
	}

	getLastChangelogVersion(): string | undefined {
		return this.settings.lastChangelogVersion;
	}

	setLastChangelogVersion(version: string): void {
		this.globalSettings.lastChangelogVersion = version;
		this.markModified("lastChangelogVersion");
		this.save();
	}

	getSessionDir(): string | undefined {
		const sessionDir = this.settings.sessionDir;
		if (!sessionDir) {
			return sessionDir;
		}
		if (sessionDir === "~") {
			return homedir();
		}
		if (sessionDir.startsWith("~/")) {
			return join(homedir(), sessionDir.slice(2));
		}
		return sessionDir;
	}

	getDefaultProvider(): string | undefined {
		return this.settings.defaultProvider;
	}

	getDefaultModel(): string | undefined {
		return this.settings.defaultModel;
	}

	setDefaultProvider(provider: string): void {
		this.globalSettings.defaultProvider = provider;
		this.markModified("defaultProvider");
		this.save();
	}

	setDefaultModel(modelId: string): void {
		this.globalSettings.defaultModel = modelId;
		this.markModified("defaultModel");
		this.save();
	}

	setDefaultModelAndProvider(provider: string, modelId: string): void {
		this.globalSettings.defaultProvider = provider;
		this.globalSettings.defaultModel = modelId;
		this.markModified("defaultProvider");
		this.markModified("defaultModel");
		this.save();
	}

	getSteeringMode(): "all" | "one-at-a-time" {
		return this.settings.steeringMode || DEFAULT_SETTINGS.steeringMode!;
	}

	setSteeringMode(mode: "all" | "one-at-a-time"): void {
		this.globalSettings.steeringMode = mode;
		this.markModified("steeringMode");
		this.save();
	}

	getFollowUpMode(): "all" | "one-at-a-time" {
		return this.settings.followUpMode || DEFAULT_SETTINGS.followUpMode!;
	}

	setFollowUpMode(mode: "all" | "one-at-a-time"): void {
		this.globalSettings.followUpMode = mode;
		this.markModified("followUpMode");
		this.save();
	}

	getTheme(): string | undefined {
		return this.settings.theme;
	}

	setTheme(theme: string): void {
		this.globalSettings.theme = theme;
		this.markModified("theme");
		this.save();
	}

	getDefaultThinkingLevel(): "off" | "minimal" | "low" | "medium" | "high" | "xhigh" | undefined {
		return this.settings.defaultThinkingLevel;
	}

	setDefaultThinkingLevel(level: "off" | "minimal" | "low" | "medium" | "high" | "xhigh"): void {
		this.globalSettings.defaultThinkingLevel = level;
		this.markModified("defaultThinkingLevel");
		this.save();
	}

	getTransport(): TransportSetting {
		return this.settings.transport ?? DEFAULT_SETTINGS.transport!;
	}

	setTransport(transport: TransportSetting): void {
		this.globalSettings.transport = transport;
		this.markModified("transport");
		this.save();
	}

	getCompactionEnabled(): boolean {
		return this.settings.compaction?.enabled ?? DEFAULT_SETTINGS.compaction!.enabled;
	}

	setCompactionEnabled(enabled: boolean): void {
		if (!this.globalSettings.compaction) {
			this.globalSettings.compaction = {};
		}
		this.globalSettings.compaction.enabled = enabled;
		this.markModified("compaction", "enabled");
		this.save();
	}

	getCompactionReserveTokens(): number {
		return this.settings.compaction?.reserveTokens ?? DEFAULT_SETTINGS.compaction!.reserveTokens;
	}

	getCompactionKeepRecentTokens(): number {
		return this.settings.compaction?.keepRecentTokens ?? DEFAULT_SETTINGS.compaction!.keepRecentTokens;
	}

	getCompactionSettings(): Required<CompactionSettings> {
		return { ...DEFAULT_SETTINGS.compaction, ...this.settings.compaction };
	}

	/**
	 * Window and threshold `/learn` mines sessions with.
	 *
	 * Each value falls back to its default unless it is a finite positive number,
	 * so a typo (`0`, `-1`, a pasted string) cannot silently reduce the window to
	 * nothing and make the command report "no recent sessions".
	 */
	getLearnSettings(): {
		maxSessions: number;
		maxAgeDays: number;
		minRepeats: number;
		minRequestRepeats: number;
		maxProposals: number;
	} {
		const positive = (value: unknown, fallback: number): number =>
			typeof value === "number" && Number.isFinite(value) && value > 0 ? Math.floor(value) : fallback;
		return {
			maxSessions: positive(this.settings.learnMaxSessions, DEFAULT_SETTINGS.learnMaxSessions),
			maxAgeDays: positive(this.settings.learnMaxAgeDays, DEFAULT_SETTINGS.learnMaxAgeDays),
			minRepeats: positive(this.settings.learnMinRepeats, DEFAULT_SETTINGS.learnMinRepeats),
			minRequestRepeats: positive(this.settings.learnMinRequestRepeats, DEFAULT_SETTINGS.learnMinRequestRepeats),
			maxProposals: positive(this.settings.learnMaxProposals, DEFAULT_SETTINGS.learnMaxProposals),
		};
	}

	/** Configured `fast`/`standard`/`capable` tiers, for callers resolving a category. */
	getModelCategories(): Settings["modelCategories"] {
		return this.settings.modelCategories ? structuredClone(this.settings.modelCategories) : undefined;
	}

	/**
	 * Persist one `/learn` threshold to the user scope.
	 *
	 * One keyed setter rather than five identical ones: the settings are flat
	 * top-level numbers that differ only in name, and the settings pane drives
	 * them from a table. Values are floored to at least 1, matching the read
	 * side's refusal to let a zero shrink the window to nothing.
	 */
	setLearnSetting(key: LearnSettingKey, value: number): void {
		const safe = Number.isFinite(value) ? Math.max(1, Math.floor(value)) : DEFAULT_SETTINGS[key];
		this.globalSettings[key] = safe;
		this.markModified(key);
		this.save();
	}

	/** Byte cap on a single read/bash tool result before truncation. Clamped to >= 1024. */
	getToolOutputMaxBytes(): number {
		const v = this.settings.toolOutput?.maxBytes ?? DEFAULT_SETTINGS.toolOutput!.maxBytes;
		return Number.isFinite(v) && v >= 1024 ? Math.floor(v) : DEFAULT_SETTINGS.toolOutput!.maxBytes;
	}

	setToolOutputMaxBytes(bytes: number): void {
		if (!this.globalSettings.toolOutput) {
			this.globalSettings.toolOutput = {};
		}
		this.globalSettings.toolOutput.maxBytes = Math.max(1024, Math.floor(bytes));
		this.markModified("toolOutput", "maxBytes");
		this.save();
	}

	/** Line cap on a single read/bash tool result before truncation. Clamped to >= 1. */
	getToolOutputMaxLines(): number {
		const v = this.settings.toolOutput?.maxLines ?? DEFAULT_SETTINGS.toolOutput!.maxLines;
		return Number.isFinite(v) && v >= 1 ? Math.floor(v) : DEFAULT_SETTINGS.toolOutput!.maxLines;
	}

	setToolOutputMaxLines(lines: number): void {
		if (!this.globalSettings.toolOutput) {
			this.globalSettings.toolOutput = {};
		}
		this.globalSettings.toolOutput.maxLines = Math.max(1, Math.floor(lines));
		this.markModified("toolOutput", "maxLines");
		this.save();
	}

	/** Whether to stub superseded read results out of the outgoing context. */
	getContextGcEnabled(): boolean {
		return this.settings.contextGc?.enabled ?? DEFAULT_SETTINGS.contextGc!.enabled;
	}

	setContextGcEnabled(enabled: boolean): void {
		if (!this.globalSettings.contextGc) {
			this.globalSettings.contextGc = {};
		}
		this.globalSettings.contextGc.enabled = enabled;
		this.markModified("contextGc", "enabled");
		this.save();
	}

	/** Trailing-silence window (ms) before voice capture auto-stops. Clamped to 300-10000. */
	getVoiceSilenceMs(): number {
		const fallback = DEFAULT_SETTINGS.voice!.silenceMs;
		const v = this.settings.voice?.silenceMs ?? fallback;
		return Number.isFinite(v) ? Math.min(10000, Math.max(300, Math.floor(v))) : fallback;
	}

	setVoiceSilenceMs(ms: number): void {
		if (!this.globalSettings.voice) {
			this.globalSettings.voice = {};
		}
		this.globalSettings.voice.silenceMs = Math.min(10000, Math.max(300, Math.floor(ms)));
		this.markModified("voice", "silenceMs");
		this.save();
	}

	/**
	 * Per-request timeout (seconds) for the webfetch/websearch binary. Settings
	 * takes precedence, then the `HOOCODE_WEBTOOLS_TIMEOUT` env var, then the
	 * default 15 (mirrors {@link getClearOnShrink}). Clamped to 1-120; a
	 * non-finite settings value or malformed env falls through to the next tier.
	 */
	getWebtoolsTimeoutSecs(): number {
		const fallback = DEFAULT_SETTINGS.webtools!.timeoutSecs;
		const clamp = (n: number): number => Math.min(120, Math.max(1, Math.floor(n)));
		const configured = this.settings.webtools?.timeoutSecs;
		if (configured !== undefined && Number.isFinite(configured)) {
			return clamp(configured);
		}
		const envRaw = process.env.HOOCODE_WEBTOOLS_TIMEOUT?.trim();
		if (envRaw) {
			const envValue = Number(envRaw);
			if (Number.isFinite(envValue) && envValue > 0) {
				return clamp(envValue);
			}
		}
		return fallback;
	}

	/**
	 * The `webtools.search` block as the binary would read it (backend choice and
	 * credentials). hoocode only reads it, to tell whether `websearch` has a keyed
	 * provider; the binary itself resolves the actual key at call time.
	 *
	 * Deliberately the user-level file rather than the merged view: the binary
	 * reads only `~/.hoocode/settings.json`, so a key in a project's
	 * `.hoocode/settings.json` never reaches it, and treating one as configured
	 * would silence the warning for a search that still runs keyless. (Contrast
	 * {@link getWebtoolsTimeoutSecs}, which is hoocode's own setting forwarded as
	 * a flag, so there the merged value is the effective one.)
	 */
	getWebtoolsSearch(): WebtoolsSearchSettings | undefined {
		return this.globalSettings.webtools?.search;
	}

	setWebtoolsTimeoutSecs(secs: number): void {
		if (!this.globalSettings.webtools) {
			this.globalSettings.webtools = {};
		}
		this.globalSettings.webtools.timeoutSecs = Math.min(120, Math.max(1, Math.floor(secs)));
		this.markModified("webtools", "timeoutSecs");
		this.save();
	}

	/** Tool names disabled from the TUI; removed from the agent in every session. */
	getDisabledTools(): string[] {
		return [...(this.settings.disabledTools ?? DEFAULT_SETTINGS.disabledTools!)];
	}

	setDisabledTools(names: string[]): void {
		this.globalSettings.disabledTools = [...names];
		this.markModified("disabledTools");
		this.save();
	}

	/**
	 * How much of a tool call the transcript shows (radar / peek / full).
	 *
	 * Settings files written before the dial existed carry the old three-value
	 * `toolOutputDisplay` key. They are read here rather than rewritten on load,
	 * so a downgrade keeps working until the user actually changes the setting.
	 */
	getToolOutputView(): ToolOutputView {
		const view = this.settings.toolOutputView;
		if (isToolOutputView(view)) return view;

		const legacy = (this.settings as { toolOutputDisplay?: unknown }).toolOutputDisplay;
		const migrated = LEGACY_TOOL_OUTPUT_VIEWS[legacy as string];
		return migrated ?? DEFAULT_SETTINGS.toolOutputView!;
	}

	setToolOutputView(view: ToolOutputView): void {
		this.globalSettings.toolOutputView = view;
		this.markModified("toolOutputView");
		this.save();
	}

	/**
	 * The chrome dial's stop, or undefined when the user has never set one.
	 *
	 * Deliberately not defaulted here. "Unset" is a real state: it is what lets
	 * a short terminal open at `compact` without that guess ever overruling a
	 * choice someone actually made. The caller resolves it (see
	 * `SMALL_TERMINAL_ROWS`), because only the caller knows the terminal size.
	 */
	getChromeDensity(): ChromeDensity | undefined {
		const density = this.settings.chromeDensity;
		return isChromeDensity(density) ? density : undefined;
	}

	setChromeDensity(density: ChromeDensity): void {
		this.globalSettings.chromeDensity = density;
		this.markModified("chromeDensity");
		this.save();
	}

	/** Persisted overrides for extension-registered flags (name -> value). */
	getFlagOverrides(): Record<string, boolean | string> {
		return { ...(this.settings.flags ?? {}) };
	}

	setFlagOverride(name: string, value: boolean | string): void {
		if (!this.globalSettings.flags) {
			this.globalSettings.flags = {};
		}
		this.globalSettings.flags[name] = value;
		this.markModified("flags", name);
		this.save();
	}

	clearFlagOverride(name: string): void {
		if (this.globalSettings.flags && name in this.globalSettings.flags) {
			delete this.globalSettings.flags[name];
			this.markModified("flags", name);
			this.save();
		}
	}

	getBranchSummarySettings(): Required<BranchSummarySettings> {
		return { ...DEFAULT_SETTINGS.branchSummary, ...this.settings.branchSummary };
	}

	getBranchSummarySkipPrompt(): boolean {
		return this.settings.branchSummary?.skipPrompt ?? DEFAULT_SETTINGS.branchSummary!.skipPrompt;
	}

	getRetryEnabled(): boolean {
		return this.settings.retry?.enabled ?? DEFAULT_SETTINGS.retry!.enabled;
	}

	setRetryEnabled(enabled: boolean): void {
		if (!this.globalSettings.retry) {
			this.globalSettings.retry = {};
		}
		this.globalSettings.retry.enabled = enabled;
		this.markModified("retry", "enabled");
		this.save();
	}

	getRetrySettings(): { enabled: boolean; maxRetries: number; baseDelayMs: number } {
		return {
			enabled: this.getRetryEnabled(),
			maxRetries: this.settings.retry?.maxRetries ?? DEFAULT_SETTINGS.retry!.maxRetries,
			baseDelayMs: this.settings.retry?.baseDelayMs ?? DEFAULT_SETTINGS.retry!.baseDelayMs,
		};
	}

	getProviderRetrySettings(): { timeoutMs?: number; maxRetries?: number; maxRetryDelayMs: number } {
		return {
			...DEFAULT_SETTINGS.retry!.provider,
			...this.settings.retry?.provider,
		};
	}

	getHideThinkingBlock(): boolean {
		return this.settings.hideThinkingBlock ?? DEFAULT_SETTINGS.hideThinkingBlock!;
	}

	setHideThinkingBlock(hide: boolean): void {
		this.globalSettings.hideThinkingBlock = hide;
		this.markModified("hideThinkingBlock");
		this.save();
	}

	getShellPath(): string | undefined {
		return this.settings.shellPath;
	}

	setShellPath(path: string | undefined): void {
		this.globalSettings.shellPath = path;
		this.markModified("shellPath");
		this.save();
	}

	getQuietStartup(): boolean {
		return this.settings.quietStartup ?? DEFAULT_SETTINGS.quietStartup!;
	}

	setQuietStartup(quiet: boolean): void {
		this.globalSettings.quietStartup = quiet;
		this.markModified("quietStartup");
		this.save();
	}

	getShellCommandPrefix(): string | undefined {
		return this.settings.shellCommandPrefix;
	}

	setShellCommandPrefix(prefix: string | undefined): void {
		this.globalSettings.shellCommandPrefix = prefix;
		this.markModified("shellCommandPrefix");
		this.save();
	}

	getNpmCommand(): string[] | undefined {
		return this.settings.npmCommand ? [...this.settings.npmCommand] : undefined;
	}

	setNpmCommand(command: string[] | undefined): void {
		this.globalSettings.npmCommand = command ? [...command] : undefined;
		this.markModified("npmCommand");
		this.save();
	}

	getCollapseChangelog(): boolean {
		return this.settings.collapseChangelog ?? DEFAULT_SETTINGS.collapseChangelog!;
	}

	setCollapseChangelog(collapse: boolean): void {
		this.globalSettings.collapseChangelog = collapse;
		this.markModified("collapseChangelog");
		this.save();
	}

	getEnableInstallTelemetry(): boolean {
		return this.settings.enableInstallTelemetry ?? DEFAULT_SETTINGS.enableInstallTelemetry!;
	}

	setEnableInstallTelemetry(enabled: boolean): void {
		this.globalSettings.enableInstallTelemetry = enabled;
		this.markModified("enableInstallTelemetry");
		this.save();
	}

	getPackages(): PackageSource[] {
		return [...(this.settings.packages ?? DEFAULT_SETTINGS.packages!)];
	}

	setPackages(packages: PackageSource[]): void {
		this.globalSettings.packages = packages;
		this.markModified("packages");
		this.save();
	}

	setProjectPackages(packages: PackageSource[]): void {
		const projectSettings = structuredClone(this.projectSettings);
		projectSettings.packages = packages;
		this.markProjectModified("packages");
		this.saveProjectSettings(projectSettings);
	}

	getExtensionPaths(): string[] {
		return [...(this.settings.extensions ?? DEFAULT_SETTINGS.extensions!)];
	}

	setExtensionPaths(paths: string[]): void {
		this.globalSettings.extensions = paths;
		this.markModified("extensions");
		this.save();
	}

	setProjectExtensionPaths(paths: string[]): void {
		const projectSettings = structuredClone(this.projectSettings);
		projectSettings.extensions = paths;
		this.markProjectModified("extensions");
		this.saveProjectSettings(projectSettings);
	}

	getSkillPaths(): string[] {
		return [...(this.settings.skills ?? DEFAULT_SETTINGS.skills!)];
	}

	setSkillPaths(paths: string[]): void {
		this.globalSettings.skills = paths;
		this.markModified("skills");
		this.save();
	}

	setProjectSkillPaths(paths: string[]): void {
		const projectSettings = structuredClone(this.projectSettings);
		projectSettings.skills = paths;
		this.markProjectModified("skills");
		this.saveProjectSettings(projectSettings);
	}

	getPromptTemplatePaths(): string[] {
		return [...(this.settings.prompts ?? DEFAULT_SETTINGS.prompts!)];
	}

	setPromptTemplatePaths(paths: string[]): void {
		this.globalSettings.prompts = paths;
		this.markModified("prompts");
		this.save();
	}

	setProjectPromptTemplatePaths(paths: string[]): void {
		const projectSettings = structuredClone(this.projectSettings);
		projectSettings.prompts = paths;
		this.markProjectModified("prompts");
		this.saveProjectSettings(projectSettings);
	}

	getSlashCommandPaths(): string[] {
		return [...(this.settings.slashCommands ?? DEFAULT_SETTINGS.slashCommands!)];
	}

	setSlashCommandPaths(paths: string[]): void {
		this.globalSettings.slashCommands = paths;
		this.markModified("slashCommands");
		this.save();
	}

	setProjectSlashCommandPaths(paths: string[]): void {
		const projectSettings = structuredClone(this.projectSettings);
		projectSettings.slashCommands = paths;
		this.markProjectModified("slashCommands");
		this.saveProjectSettings(projectSettings);
	}

	getThemePaths(): string[] {
		return [...(this.settings.themes ?? DEFAULT_SETTINGS.themes!)];
	}

	setThemePaths(paths: string[]): void {
		this.globalSettings.themes = paths;
		this.markModified("themes");
		this.save();
	}

	setProjectThemePaths(paths: string[]): void {
		const projectSettings = structuredClone(this.projectSettings);
		projectSettings.themes = paths;
		this.markProjectModified("themes");
		this.saveProjectSettings(projectSettings);
	}

	getEnableSkillCommands(): boolean {
		return this.settings.enableSkillCommands ?? DEFAULT_SETTINGS.enableSkillCommands!;
	}

	setEnableSkillCommands(enabled: boolean): void {
		this.globalSettings.enableSkillCommands = enabled;
		this.markModified("enableSkillCommands");
		this.save();
	}

	getEnableSubagent(): boolean {
		return this.settings.enableSubagent ?? DEFAULT_SETTINGS.enableSubagent!;
	}

	setEnableSubagent(enabled: boolean): void {
		this.globalSettings.enableSubagent = enabled;
		this.markModified("enableSubagent");
		this.save();
	}

	/** Whether to dispatch eligible subagents on reused warm workers (experimental). */
	getWarmSubagents(): boolean {
		return this.settings.warmSubagents ?? DEFAULT_SETTINGS.warmSubagents!;
	}

	setWarmSubagents(enabled: boolean): void {
		this.globalSettings.warmSubagents = enabled;
		this.markModified("warmSubagents");
		this.save();
	}

	/** Tree-wide subagent nesting cap. Clamped to >= 1 (delegation can never be disabled here). */
	getMaxSubagentDepth(): number {
		const v = this.settings.maxSubagentDepth ?? DEFAULT_SETTINGS.maxSubagentDepth!;
		return Number.isFinite(v) && v >= 1 ? Math.floor(v) : 1;
	}

	/** Max concurrent subagents per pool at nesting depth >= 1. Clamped to >= 1. */
	getNestedSubagentConcurrency(): number {
		const v = this.settings.nestedSubagentConcurrency ?? DEFAULT_SETTINGS.nestedSubagentConcurrency!;
		return Number.isFinite(v) && v >= 1 ? Math.floor(v) : 2;
	}

	getEnableTodoWrite(): boolean {
		return this.settings.enableTodoWrite ?? DEFAULT_SETTINGS.enableTodoWrite!;
	}

	setEnableTodoWrite(enabled: boolean): void {
		this.globalSettings.enableTodoWrite = enabled;
		this.markModified("enableTodoWrite");
		this.save();
	}

	/** Raw `platform` tokens (string coerced to an array), or undefined when unset. */
	getPlatform(): string[] | undefined {
		const value = this.settings.platform;
		if (value === undefined) return undefined;
		return Array.isArray(value) ? value : [value];
	}

	/**
	 * Persist the artifact platform targets. Always stored as an array (getPlatform
	 * accepts either shape), and an empty/undefined list removes the key so the
	 * per-consumer defaults apply again rather than an empty list being read as
	 * "target nothing". Global scope: the layout a machine writes is an
	 * environment-level choice, not a per-repo one.
	 */
	setPlatform(platforms: readonly string[] | undefined): void {
		this.globalSettings.platform = platforms && platforms.length > 0 ? [...platforms] : undefined;
		this.markModified("platform");
		this.save();
	}

	getEnablePluginTools(): boolean {
		return this.settings.enablePluginTools ?? DEFAULT_SETTINGS.enablePluginTools!;
	}

	setEnablePluginTools(enabled: boolean): void {
		this.globalSettings.enablePluginTools = enabled;
		this.markModified("enablePluginTools");
		this.save();
	}

	/**
	 * Where an autonomous InstallPlugin puts a plugin. `/plugin install` asks the
	 * human instead of reading this — the setting exists precisely for the path
	 * where there is nobody to ask.
	 */
	getPluginInstallScope(): "user" | "project" {
		const value = this.settings.pluginInstallScope;
		return value === "project" ? "project" : DEFAULT_SETTINGS.pluginInstallScope!;
	}

	setPluginInstallScope(scope: "user" | "project"): void {
		this.globalSettings.pluginInstallScope = scope;
		this.markModified("pluginInstallScope");
		this.save();
	}

	getDeferMcpSchemas(): boolean {
		return this.settings.deferMcpSchemas ?? DEFAULT_SETTINGS.deferMcpSchemas!;
	}

	setDeferMcpSchemas(enabled: boolean): void {
		this.globalSettings.deferMcpSchemas = enabled;
		this.markModified("deferMcpSchemas");
		this.save();
	}

	getEnableWebTools(): boolean {
		return this.settings.enableWebTools ?? DEFAULT_SETTINGS.enableWebTools!;
	}

	setEnableWebTools(enabled: boolean): void {
		this.globalSettings.enableWebTools = enabled;
		this.markModified("enableWebTools");
		this.save();
	}

	getEnableSemanticIndex(): boolean {
		return this.settings.enableSemanticIndex ?? DEFAULT_SETTINGS.enableSemanticIndex!;
	}

	setEnableSemanticIndex(enabled: boolean): void {
		this.globalSettings.enableSemanticIndex = enabled;
		this.markModified("enableSemanticIndex");
		this.save();
	}

	getEmbsearchBinaryPath(): string | undefined {
		return this.settings.embsearchBinaryPath;
	}

	getEmbsearchThresholdBytes(): number {
		return this.settings.embsearchThresholdBytes ?? DEFAULT_SETTINGS.embsearchThresholdBytes!;
	}

	getLight(): boolean {
		return this.settings.light ?? DEFAULT_SETTINGS.light!;
	}

	setLight(enabled: boolean): void {
		this.globalSettings.light = enabled;
		this.markModified("light");
		this.save();
	}

	getThinkingBudgets(): ThinkingBudgetsSettings | undefined {
		return this.settings.thinkingBudgets;
	}

	getThinkingDisplay(): "summarized" | "omitted" | undefined {
		return this.settings.thinkingDisplay;
	}

	getShowImages(): boolean {
		return this.settings.terminal?.showImages ?? DEFAULT_SETTINGS.terminal!.showImages;
	}

	setShowImages(show: boolean): void {
		if (!this.globalSettings.terminal) {
			this.globalSettings.terminal = {};
		}
		this.globalSettings.terminal.showImages = show;
		this.markModified("terminal", "showImages");
		this.save();
	}

	getImageWidthCells(): number {
		const width = this.settings.terminal?.imageWidthCells ?? DEFAULT_SETTINGS.terminal!.imageWidthCells;
		if (typeof width !== "number" || !Number.isFinite(width)) {
			return DEFAULT_SETTINGS.terminal!.imageWidthCells;
		}
		return Math.max(1, Math.floor(width));
	}

	setImageWidthCells(width: number): void {
		if (!this.globalSettings.terminal) {
			this.globalSettings.terminal = {};
		}
		this.globalSettings.terminal.imageWidthCells = Math.max(1, Math.floor(width));
		this.markModified("terminal", "imageWidthCells");
		this.save();
	}

	getClearOnShrink(): boolean {
		// Settings takes precedence, then env var, then default false
		if (this.settings.terminal?.clearOnShrink !== undefined) {
			return this.settings.terminal.clearOnShrink;
		}
		return process.env.HOOCODE_CLEAR_ON_SHRINK === "1";
	}

	setClearOnShrink(enabled: boolean): void {
		if (!this.globalSettings.terminal) {
			this.globalSettings.terminal = {};
		}
		this.globalSettings.terminal.clearOnShrink = enabled;
		this.markModified("terminal", "clearOnShrink");
		this.save();
	}

	getShowTerminalProgress(): boolean {
		return this.settings.terminal?.showTerminalProgress ?? DEFAULT_SETTINGS.terminal!.showTerminalProgress;
	}

	setShowTerminalProgress(enabled: boolean): void {
		if (!this.globalSettings.terminal) {
			this.globalSettings.terminal = {};
		}
		this.globalSettings.terminal.showTerminalProgress = enabled;
		this.markModified("terminal", "showTerminalProgress");
		this.save();
	}

	getChimeOnTurnComplete(): boolean {
		return this.settings.terminal?.chimeOnTurnComplete ?? DEFAULT_SETTINGS.terminal!.chimeOnTurnComplete;
	}

	setChimeOnTurnComplete(enabled: boolean): void {
		if (!this.globalSettings.terminal) {
			this.globalSettings.terminal = {};
		}
		this.globalSettings.terminal.chimeOnTurnComplete = enabled;
		this.markModified("terminal", "chimeOnTurnComplete");
		this.save();
	}

	// --- Tips ---------------------------------------------------------------
	// `seen` and `starNudges` are written by the tip rotation as a side effect of
	// showing something, so these setters are called from a timer rather than
	// from a user action. They stay cheap: a bounded array and an integer.

	getTipsEnabled(): boolean {
		return this.settings.tips?.enabled ?? DEFAULT_SETTINGS.tips!.enabled!;
	}

	setTipsEnabled(enabled: boolean): void {
		if (!this.globalSettings.tips) {
			this.globalSettings.tips = {};
		}
		this.globalSettings.tips.enabled = enabled;
		this.markModified("tips", "enabled");
		this.save();
	}

	getSeenTips(): string[] {
		return this.settings.tips?.seen ?? [];
	}

	/**
	 * Remember that a tip has been shown.
	 *
	 * Idempotent, so a tip repeated in a long session does not grow the list. The
	 * list is bounded by the number of tips that exist, and an id that is no
	 * longer in `TIPS` is harmless -- it is only ever tested for membership.
	 */
	markTipSeen(id: string): void {
		if (!this.globalSettings.tips) {
			this.globalSettings.tips = {};
		}
		const seen = this.globalSettings.tips.seen ?? [];
		if (seen.includes(id)) return;
		this.globalSettings.tips.seen = [...seen, id];
		this.markModified("tips", "seen");
		this.save();
	}

	getStarNudgeCount(): number {
		return this.settings.tips?.starNudges ?? 0;
	}

	recordStarNudge(): void {
		if (!this.globalSettings.tips) {
			this.globalSettings.tips = {};
		}
		this.globalSettings.tips.starNudges = (this.globalSettings.tips.starNudges ?? 0) + 1;
		this.markModified("tips", "starNudges");
		this.save();
	}

	getImageAutoResize(): boolean {
		return this.settings.images?.autoResize ?? DEFAULT_SETTINGS.images!.autoResize;
	}

	setImageAutoResize(enabled: boolean): void {
		if (!this.globalSettings.images) {
			this.globalSettings.images = {};
		}
		this.globalSettings.images.autoResize = enabled;
		this.markModified("images", "autoResize");
		this.save();
	}

	getBlockImages(): boolean {
		return this.settings.images?.blockImages ?? DEFAULT_SETTINGS.images!.blockImages;
	}

	setBlockImages(blocked: boolean): void {
		if (!this.globalSettings.images) {
			this.globalSettings.images = {};
		}
		this.globalSettings.images.blockImages = blocked;
		this.markModified("images", "blockImages");
		this.save();
	}

	getEnabledModels(): string[] | undefined {
		return this.settings.enabledModels;
	}

	setEnabledModels(patterns: string[] | undefined): void {
		this.globalSettings.enabledModels = patterns;
		this.markModified("enabledModels");
		this.save();
	}

	getDoubleEscapeAction(): "fork" | "tree" | "none" {
		return this.settings.doubleEscapeAction ?? DEFAULT_SETTINGS.doubleEscapeAction!;
	}

	setDoubleEscapeAction(action: "fork" | "tree" | "none"): void {
		this.globalSettings.doubleEscapeAction = action;
		this.markModified("doubleEscapeAction");
		this.save();
	}

	getTreeFilterMode(): "default" | "no-tools" | "user-only" | "labeled-only" | "all" {
		const mode = this.settings.treeFilterMode;
		const valid = ["default", "no-tools", "user-only", "labeled-only", "all"];
		return mode && valid.includes(mode) ? mode : DEFAULT_SETTINGS.treeFilterMode!;
	}

	setTreeFilterMode(mode: "default" | "no-tools" | "user-only" | "labeled-only" | "all"): void {
		this.globalSettings.treeFilterMode = mode;
		this.markModified("treeFilterMode");
		this.save();
	}

	getShowHardwareCursor(): boolean {
		return this.settings.showHardwareCursor ?? process.env.HOOCODE_HARDWARE_CURSOR === "1";
	}

	setShowHardwareCursor(enabled: boolean): void {
		this.globalSettings.showHardwareCursor = enabled;
		this.markModified("showHardwareCursor");
		this.save();
	}

	getEditorBorder(): "rule" | "box" {
		return this.settings.editorBorder ?? DEFAULT_SETTINGS.editorBorder!;
	}

	setEditorBorder(border: "rule" | "box"): void {
		this.globalSettings.editorBorder = border;
		this.markModified("editorBorder");
		this.save();
	}

	getEditorPaddingX(): number {
		return this.settings.editorPaddingX ?? DEFAULT_SETTINGS.editorPaddingX!;
	}

	setEditorPaddingX(padding: number): void {
		this.globalSettings.editorPaddingX = Math.max(0, Math.min(3, Math.floor(padding)));
		this.markModified("editorPaddingX");
		this.save();
	}

	getAutocompleteMaxVisible(): number {
		return this.settings.autocompleteMaxVisible ?? DEFAULT_SETTINGS.autocompleteMaxVisible!;
	}

	setAutocompleteMaxVisible(maxVisible: number): void {
		this.globalSettings.autocompleteMaxVisible = Math.max(3, Math.min(20, Math.floor(maxVisible)));
		this.markModified("autocompleteMaxVisible");
		this.save();
	}

	getCodeBlockIndent(): string {
		return this.settings.markdown?.codeBlockIndent ?? DEFAULT_SETTINGS.markdown!.codeBlockIndent;
	}

	getWarnings(): WarningSettings {
		return { ...DEFAULT_SETTINGS.warnings, ...this.settings.warnings };
	}

	setWarnings(warnings: WarningSettings): void {
		this.globalSettings.warnings = { ...warnings };
		this.markModified("warnings");
		this.save();
	}
}
